// CollapsibleSection.js - Reusable progressive-disclosure section for Asset Details Redux (PRD-43)

import React, { useState, useCallback, useId } from 'react';
import './CollapsibleSection.css';

/**
 * @param {object} props
 * @param {string} props.title - Section heading
 * @param {string} [props.subtitle] - Explanatory line under the title
 * @param {boolean} [props.defaultOpen] - Initial open state (default true)
 * @param {string} [props.badge] - Optional badge text (e.g. count)
 * @param {'critical'|'high'|'medium'|'low'|'info'|'neutral'} [props.badgeTone] - Badge color band
 * @param {string} [props.emptyMessage] - Shown when children is empty
 * @param {React.ReactNode} props.children
 * @param {string} [props.className]
 */
function CollapsibleSection({
    title,
    subtitle,
    defaultOpen = true,
    badge,
    badgeTone = 'neutral',
    emptyMessage,
    children,
    className = ''
}) {
    const [open, setOpen] = useState(defaultOpen);
    const headingId = useId();
    const contentId = useId();

    const toggle = useCallback(() => setOpen((v) => !v), []);
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
        }
    };

    const isEmpty = React.Children.count(children) === 0;

    return (
        <section className={`collapsible-section ${open ? 'collapsible-section--open' : ''} ${className}`}>
            <header
                className="collapsible-section__header"
                role="button"
                tabIndex={0}
                aria-expanded={open}
                aria-controls={contentId}
                onClick={toggle}
                onKeyDown={handleKeyDown}
            >
                <span className={`collapsible-section__chevron ${open ? 'is-open' : ''}`} aria-hidden="true">
                    ▸
                </span>
                <div className="collapsible-section__heading">
                    <h3 id={headingId} className="collapsible-section__title">{title}</h3>
                    {subtitle && <p className="collapsible-section__subtitle">{subtitle}</p>}
                </div>
                {badge != null && (
                    <span className={`collapsible-section__badge collapsible-section__badge--${badgeTone}`}>
                        {badge}
                    </span>
                )}
            </header>
            {open && (
                <div id={contentId} className="collapsible-section__content" aria-labelledby={headingId}>
                    {isEmpty ? (
                        <div className="collapsible-section__empty">{emptyMessage || 'No data available.'}</div>
                    ) : (
                        children
                    )}
                </div>
            )}
        </section>
    );
}

export default CollapsibleSection;
